import { Observable, Subscription } from 'rxjs';

import { Component, DestroyRef, effect, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

import { ResourcesService } from '@mitsubishi/core';
import { FormattingUtils } from '@mitsubishi/utils';
import { ApmFieldErrorsComponent } from '@mitsubishi/shared/ui';
import { InlineSpinnerComponent } from '@mitsubishi/ui/spinner';

import { isNil } from '@angular-ru/cdk/utils';

import { FreestFieldMappingDto, UomSystem } from '../../models/chiller-free-st.model';
import { FreeStUtils } from '../../chiller-free-st.utils';

@Component({
  selector: 'apm-convertible-input',
  imports: [ApmFieldErrorsComponent, InlineSpinnerComponent],
  template: `
    <div class="convertible-input-wrapper">
      <input
        #inputEl
        type="text"
        inputmode="decimal"
        class="apm-input"
        [class.ng-invalid]="ngControl?.invalid"
        [class.ng-touched]="ngControl?.touched"
        [class.ng-dirty]="ngControl?.dirty"
        [class.is-computing]="isComputing()"
        [value]="displayValue()"
        [disabled]="isDisabled()"
        (input)="onInput($event)"
        (focus)="onFocus()"
        (blur)="onBlur()"
      />
      @if (showSpinner()) {
        <apm-inline-spinner class="convertible-input-spinner" />
      }
    </div>
    <apm-field-errors [control]="ngControl?.control ?? null" [customMessages]="customErrorMessages"></apm-field-errors>
  `,
  styles: `
    .convertible-input-wrapper {
      position: relative;
    }
    .convertible-input-spinner {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
    }
    input.is-computing {
      padding-right: 32px;
    }
  `,
})
export class ConvertibleInputComponent implements ControlValueAccessor {
  readonly mapping = input<FreestFieldMappingDto>();
  readonly uomSystem = input<UomSystem>('SI');
  readonly computeFn = input<(value: number, sourceUom: UomSystem) => Observable<number>>();

  readonly displayValue = signal('');
  readonly isDisabled = signal(false);
  readonly isComputing = signal(false);
  readonly showSpinner = signal(false);

  readonly ngControl = inject(NgControl, { self: true, optional: true });
  #resourcesService = inject(ResourcesService);
  #destroyRef = inject(DestroyRef);

  readonly customErrorMessages = {
    tempDiff: (err: { min?: number; max?: number; actual: number }) =>
      this.#resourcesService.getTextResource(451857, {
        placeholderValues: [err.actual.toFixed(2), err.min ?? '-', err.max ?? '-'],
      }),
    computeError: () => this.#resourcesService.getTextResource(451859),
  };

  private readonly inputEl = viewChild<ElementRef<HTMLInputElement>>('inputEl');

  private siValue: number | null = null;
  private editing = false;
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};
  private blurSub: Subscription | null = null;
  private displaySub: Subscription | null = null;
  private spinnerTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    effect(() => {
      const _mapping = this.mapping();
      const _uom = this.uomSystem();
      if (!this.editing) {
        this.refreshDisplay();
      }
    });

    this.#destroyRef.onDestroy(() => this.cancelAll());
  }

  writeValue(value: number | null): void {
    this.siValue = value;
    if (!this.editing) {
      this.refreshDisplay();
    }
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled.set(disabled);
  }

  onInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    const filtered = raw.replace(/[^0-9.\-]/g, '');
    if (filtered !== raw) {
      (event.target as HTMLInputElement).value = filtered;
    }
  }

  onFocus(): void {
    this.editing = true;
    this.cancelAll();
    this.clearComputeError();
    const el = this.inputEl()?.nativeElement;
    if (el) {
      setTimeout(() => el.select());
    }
  }

  onBlur(): void {
    this.editing = false;
    this.onTouched();

    const raw = this.inputEl()?.nativeElement.value ?? '';
    const parsed = parseFloat(raw);

    if (raw === '' || isNaN(parsed)) {
      this.siValue = null;
      this.onChange(null);
      this.displayValue.set('');
      return;
    }

    const fn = this.computeFn();
    if (fn && this.uomSystem() !== 'SI') {
      this.computeToSI(parsed, fn);
    } else {
      this.siValue = fn ? parsed : this.toSI(parsed);
      this.onChange(this.siValue);
      this.displayValue.set(this.formatForDisplay(this.siValue));
    }
  }

  private computeToSI(inputValue: number, fn: (value: number, sourceUom: UomSystem) => Observable<number>): void {
    const sourceUom = this.uomSystem();
    this.cancelBlur();
    this.startSpinner();
    this.blurSub = fn(inputValue, sourceUom).subscribe({
      next: siValue => {
        this.blurSub = null;
        this.stopSpinner();
        this.siValue = siValue;
        this.onChange(this.siValue);
        if (this.uomSystem() === sourceUom) {
          this.displayValue.set(this.formatDisplayVal(inputValue));
        }
      },
      error: () => {
        this.blurSub = null;
        this.stopSpinner();
        this.ngControl?.control?.setErrors({
          ...this.ngControl.control.errors,
          computeError: true,
        });
      },
    });
  }

  private refreshDisplay(): void {
    if (this.siValue === null) {
      this.displayValue.set('');
      return;
    }

    const fn = this.computeFn();
    if (fn && this.uomSystem() !== 'SI') {
      this.cancelDisplay();
      this.displaySub = fn(this.siValue, 'SI').subscribe({
        next: displayVal => {
          this.displaySub = null;
          this.displayValue.set(this.formatDisplayVal(displayVal));
        },
        error: () => {
          this.displaySub = null;
          this.displayValue.set(String(this.siValue));
        },
      });
    } else {
      this.displayValue.set(this.formatForDisplay(this.siValue));
    }
  }

  private formatDisplayVal(value: number): string {
    const m = this.mapping();
    if (!m) return String(value);
    const formatting = FreeStUtils.getFormatting(m, this.uomSystem());
    return FormattingUtils.formatOutputValue(formatting, value) ?? String(value);
  }

  private startSpinner(): void {
    this.isComputing.set(true);
    this.spinnerTimer = setTimeout(() => this.showSpinner.set(true), 250);
  }

  private stopSpinner(): void {
    if (this.spinnerTimer) clearTimeout(this.spinnerTimer);
    this.spinnerTimer = null;
    this.isComputing.set(false);
    this.showSpinner.set(false);
  }

  private cancelBlur(): void {
    this.blurSub?.unsubscribe();
    this.blurSub = null;
    this.stopSpinner();
  }

  private cancelDisplay(): void {
    this.displaySub?.unsubscribe();
    this.displaySub = null;
  }

  private cancelAll(): void {
    this.cancelBlur();
    this.cancelDisplay();
  }

  private clearComputeError(): void {
    const control = this.ngControl?.control;
    if (control?.hasError('computeError')) {
      const { computeError: _, ...rest } = control.errors ?? {};
      control.setErrors(Object.keys(rest).length ? rest : null);
    }
  }

  private formatForDisplay(siValue: number | null): string {
    if (siValue === null) return '';
    const m = this.mapping();
    if (!m) return String(siValue);

    let displayVal = siValue;
    // The form control holds the SI (canonical) value. Only translate it for display
    // when showing a non-SI (IP) unit; in SI mode the displayed value is the value itself.
    if (this.uomSystem() !== 'SI' && !isNil(m.conversionParamA) && !isNil(m.conversionParamB)) {
      displayVal = FormattingUtils.toDisplayValue(siValue, m.conversionParamA, m.conversionParamB);
    }

    const formatting = FreeStUtils.getFormatting(m, this.uomSystem());
    const formatted = FormattingUtils.formatOutputValue(formatting, displayVal);
    return formatted ?? String(displayVal);
  }

  private toSI(displayValue: number): number {
    const m = this.mapping();
    // The canonical value stored in the form control is the SI value used by the limits,
    // standard conditions and the backend. In SI mode that is exactly what the user types,
    // so no conversion is applied; the mapping params only translate a non-SI (IP) input.
    if (this.uomSystem() === 'SI' || !m || isNil(m.conversionParamA) || isNil(m.conversionParamB)) {
      return displayValue;
    }
    return FormattingUtils.toBaseValue(displayValue, m.conversionParamA, m.conversionParamB);
  }
}
